import { useEffect, useMemo, useState } from "react";
import { Copy, Eye, MessageSquare, UsersRound } from "lucide-react";
import { toast } from "sonner";

import { api, getErrorMessage } from "@/api";
import CampaignShareTools from "@/components/CampaignShareTools";

export default function ProfileInsights({ profile, profileSlug, onSlugChange }) {
  const [analytics, setAnalytics] = useState(null);
  const [slug, setSlug] = useState(profileSlug || "");
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");

  const loadAnalytics = async () => {
    setLoading(true);
    setLoadError("");
    try {
      const { data } = await api.get("/portfolio/analytics");
      setAnalytics(data);
    } catch (error) {
      setLoadError("Insights are unavailable right now.");
      toast.error(getErrorMessage(error));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setSlug(profileSlug || "");
  }, [profileSlug]);

  useEffect(() => {
    loadAnalytics();
  }, []);

  const shareUrl = `${window.location.origin}/talent/${slug}`;
  const peak = useMemo(() => Math.max(...(analytics?.trend || []).map((item) => item.views), 1), [analytics]);
  const formatSource = (source) => source.replace(/-/g, " ").replace(/\b\w/g, (letter) => letter.toUpperCase());

  const saveSlug = async (event) => {
    event.preventDefault();
    const validSlug = /^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(slug) && slug.length >= 3;
    if (!validSlug) {
      toast.error("Use 3–50 lowercase letters, numbers, and hyphens.");
      return;
    }
    try {
      const { data } = await api.patch("/portfolio/slug", { slug });
      onSlugChange(data.profile_slug);
      await loadAnalytics();
      toast.success("Portfolio link updated.");
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  };

  const copyLink = async () => {
    await navigator.clipboard.writeText(shareUrl);
    toast.success("Portfolio link copied.");
  };

  return <section className="profile-insights" data-testid="profile-insights-panel">
    <div className="insights-heading"><div><p className="eyebrow">SHARE & INSIGHTS</p><h2>Your portfolio, in motion.</h2></div><button data-testid="copy-profile-link-button" onClick={copyLink}><Copy size={15} /> Copy link</button></div>
    <form className="custom-link-form" data-testid="custom-profile-link-form" onSubmit={saveSlug}><label>crewed.com/talent/<input data-testid="custom-profile-slug-input" onChange={(event) => setSlug(event.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ""))} value={slug} /></label><button className="outline-button" data-testid="save-custom-profile-link-button">Save link</button></form><p className="slug-hint" data-testid="custom-profile-slug-hint">Use 3–50 lowercase letters, numbers, and hyphens.</p>
    {loading && <p className="insights-state" data-testid="analytics-loading">Loading insights…</p>}
    {loadError && <p className="insights-state" data-testid="analytics-error">{loadError}</p>}
    {analytics && !loading && <><div className="insight-stats"><div data-testid="analytics-total-views"><Eye size={17} /><strong>{analytics.total_views}</strong><span>profile views</span></div><div data-testid="analytics-unique-visitors"><UsersRound size={17} /><strong>{analytics.unique_visitors}</strong><span>unique visitors</span></div><div data-testid="analytics-inquiry-clicks"><MessageSquare size={17} /><strong>{analytics.inquiry_clicks}</strong><span>inquiry clicks</span></div></div><div className="trend-label"><span>Views — last 30 days</span><span>{peak} peak day</span></div><div className="trend-chart" data-testid="analytics-trend-chart">{analytics.trend.map((item) => <span data-testid={`analytics-day-${item.day}`} key={item.day} style={{ height: `${Math.max(4, (item.views / peak) * 100)}%` }} title={`${item.day}: ${item.views} views`} />)}</div><div className="trend-dates"><span>30 days ago</span><span>Today</span></div><div className="source-list" data-testid="analytics-source-list">{analytics.sources.length ? analytics.sources.map((source) => <span data-testid={`analytics-source-${source.label}`} key={source.label}>{formatSource(source.label)} · {source.count}</span>) : <span>No traffic sources yet.</span>}</div><CampaignShareTools profile={profile} profileSlug={slug} /></>}
  </section>;
}